import { createContext, useCallback, useContext, useState } from "react";
import UserPreviewSheet from "@/components/UserPreviewSheet";

/**
 * UserBadge / UserBadgeProvider — the reusable primitive for making any
 * user identity render tappable. Two behaviours driven by the wrapping
 * `<UserBadgeProvider>`:
 *
 *   • `context="live"` (default when inside a LiveRoom) — opens the shared
 *     `<UserPreviewSheet>` in-place. Live playback continues underneath
 *     because the sheet is a portal to <body> and does not touch the
 *     LiveRoomStage tree.
 *
 *   • `context="page"` — the badge navigates to the full profile route
 *     `/u/:handle` directly (no modal). Used on non-Live surfaces where
 *     there is nothing critical playing behind the tap.
 *
 * The provider owns exactly ONE `<UserPreviewSheet>` per subtree, so
 * many badges can share it without instantiating multiple sheets.
 */

const UserBadgeCtx = createContext({
  context: "page",
  openPreview: () => {},
});

export function UserBadgeProvider({ context = "live", children }) {
  const [openRef, setOpenRef] = useState(null);
  const openPreview = useCallback((ref) => setOpenRef(ref || null), []);
  const close = useCallback(() => setOpenRef(null), []);
  return (
    <UserBadgeCtx.Provider value={{ context, openPreview }}>
      {children}
      {/* Single shared sheet for the entire provider subtree. */}
      <UserPreviewSheet userRef={openRef} open={!!openRef} onClose={close} />
    </UserBadgeCtx.Provider>
  );
}

export function useUserBadge() {
  return useContext(UserBadgeCtx);
}

/**
 * <UserBadge userRef={u.id || u.handle}>{children}</UserBadge>
 *
 * `userRef` accepts either handle or user id (backend `/preview` endpoint
 * resolves both). If neither is provided the badge just renders its
 * children inertly — safe fallback for defensive callers.
 *
 * `href` is optional and only used when `context="page"` — the anchor
 * is rendered directly to preserve middle-click / long-press behaviors
 * on non-Live surfaces.
 */
export default function UserBadge({
  userRef,
  href,
  className = "",
  as: As = "button",
  onClick: extraOnClick,
  children,
  "data-testid": dataTestId,
  ...rest
}) {
  const { context, openPreview } = useUserBadge();

  if (!userRef) {
    // Nothing to tap into — render children inertly so the caller doesn't
    // have to special-case anonymous/system entries.
    return <span className={className} {...rest}>{children}</span>;
  }

  if (context === "page" && href) {
    // Non-Live surface with a page-navigation target — render a real
    // anchor so browsers keep native behaviors.
    return (
      <a
        href={href}
        className={className}
        data-testid={dataTestId}
        onClick={(e) => { extraOnClick?.(e); }}
        {...rest}
      >
        {children}
      </a>
    );
  }

  // Default: open the preview sheet without navigating away.
  return (
    <As
      type={As === "button" ? "button" : undefined}
      className={className}
      data-testid={dataTestId}
      onClick={(e) => {
        e.stopPropagation();
        extraOnClick?.(e);
        openPreview(userRef);
      }}
      {...rest}
    >
      {children}
    </As>
  );
}
